import { Button, Container, Stack, Typography } from '@mui/material';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';

import { isDevelopment } from '@/shared/config/env';

/**
 * Rendered by the router whenever a route module throws — including render
 * errors and failed loaders. Without an `errorElement`, React Router unmounts
 * the whole tree and the user gets a blank white screen.
 */
export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  const status = isRouteErrorResponse(error) ? error.status : undefined;
  const title = status === 404 ? 'Page not found' : 'Something went wrong';
  const detail =
    status === 404
      ? 'The page you were looking for does not exist or has moved.'
      : 'The page failed to load. Trying again usually resolves it.';

  return (
    <Container maxWidth="sm" className="flex min-h-dvh items-center py-10">
      <Stack spacing={3} className="w-full" role="alert">
        <Stack spacing={1}>
          <Typography variant="h2" component="h1">
            {title}
          </Typography>
          <Typography color="text.secondary">{detail}</Typography>
        </Stack>

        {/* Stack on phones, inline from `sm` up. Full-width buttons are far
            easier to hit one-handed than side-by-side ones. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="contained" onClick={() => void navigate(0)}>
            Try again
          </Button>
          <Button variant="outlined" onClick={() => void navigate('/', { replace: true })}>
            Back to dashboard
          </Button>
        </Stack>

        {/* The stack trace helps a developer and frightens everyone else. */}
        {isDevelopment && error instanceof Error && (
          <Typography
            component="pre"
            variant="body2"
            className="overflow-x-auto rounded-lg bg-canvas p-3 text-content-muted"
          >
            {error.stack ?? error.message}
          </Typography>
        )}
      </Stack>
    </Container>
  );
};
